import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  APIProvider, Map, Marker, useMap,
} from "@vis.gl/react-google-maps";
import axios from "axios";
import RestaurantForm from "./RestaurantForm";
import RestaurantSearch from "./RestaurantSearch";
import { usePlaceRegister } from "../hooks/usePlaceRegister";
import KakaoShareButton from "./KakaoShareButton";

const MapInstanceSync = ({ setMapInstance }) => {
  const map = useMap();
  useEffect(() => { if (map) setMapInstance(map); }, [map]);
  return null;
};
const MapView = ({ city, onBack, user, sharedRestaurantId }) => {
  const [savedRestaurants, setSavedRestaurants] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [newRestaurantPosition, setNewRestaurantPosition] = useState(null);
  const [newRestaurantAddress, setNewRestaurantAddress] = useState("");
  const [targetCenter, setTargetCenter] = useState(null);
  const [targetZoom, setTargetZoom] = useState(null);
  const [searchMarker, setSearchMarker] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);

  //const { registerFromPlace } = usePlaceRegister(mapInstance);
  const { registerFromPlace } = usePlaceRegister();
  const [initialFormData, setInitialFormData] = useState(null);
  const [editingRestaurantId, setEditingRestaurantId] = useState(null);
  const ADMIN_KAKAO_ID = "4792660546";
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isMobile = window.innerWidth <= 768;
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [showMyOnly, setShowMyOnly] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [visitedIds, setVisitedIds] = useState([]);

  // ✅ 카테고리 필터 state 추가
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("전체");
  const [myLocation, setMyLocation] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState("");

  const [debugLog, setDebugLog] = useState([]);
  const addLog = (msg) => setDebugLog(prev => [...prev, `${new Date().toLocaleTimeString()} ${msg}`]);

  const cityBounds = useMemo(() => {
    return city.bounds ? {
      north: city.bounds.north,
      south: city.bounds.south,
      east: city.bounds.east,
      west: city.bounds.west,
    } : null;
  }, [city.bounds]);

  const fetchSavedRestaurants = useCallback(async () => {
    try {
      const url = selectedCategory === "전체"
        ? `/api/restaurants/city/${city.name}`
        : `/api/restaurants/city/${city.name}/filter?category=${encodeURIComponent(selectedCategory)}`;
      const response = await axios.get(url);
      setSavedRestaurants(response.data);
    } catch (error) {
      console.error('맛집 목록 가져오기 실패:', error);
    }
  }, [city.name, selectedCategory]);  // ✅ selectedCategory 의존성 추가

  // ✅ 카테고리 목록 불러오기
  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`/api/restaurants/city/${city.name}/categories`);
      setCategories(["전체", ...res.data]);
    } catch (e) {
      console.error("카테고리 목록 조회 실패:", e);
    }
  }, [city.name]);



  useEffect(() => {
    fetchSavedRestaurants();
  }, [fetchSavedRestaurants]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const getAddressFromLatLng = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
        { headers: { 'User-Agent': 'RestaurantMapApp/1.0' } }
      );
      const data = await response.json();
      return data.display_name || "";
    } catch (error) {
      return "";
    }
  };

  const handlePlaceSelect = useCallback((place) => {
    // ⭐ 배열(DB 검색 결과 여러 개)이면 사이드바 열기
    if (Array.isArray(place)) {
      if (place.length === 0) {
        alert('검색 결과가 없습니다.');
        return;
      }
      // 첫 번째 결과로 지도 이동
      const first = place[0];
      setTargetCenter({ lat: first.lat, lng: first.lng });
      setTargetZoom(15);
      setSearchMarker(null);
      setSearchResults(place);   // ⭐ 검색 결과 저장
      setIsSidebarOpen(true);
      return;
    }

    console.log("📍 handlePlaceSelect place:", place);  // ✅ 추가
    console.log("📍 placeId:", place.placeId);           // ✅ 추가
    const lat = place.lat || place.location?.latitude;
    const lng = place.lng || place.location?.longitude;
    if (cityBounds && !isWithinBounds(lat, lng, cityBounds)) {
      alert(`${city.name} 내에서만 검색됩니다!`);
      return;
    }
    setTargetCenter({ lat, lng });
    setTargetZoom(17);
    setSearchMarker({ lat, lng, name: place.name, address: place.address, placeId: place.placeId || place.id, isDB: typeof place.id === 'number' });
  }, [cityBounds, city.name]);

  const isWithinBounds = (lat, lng, bounds) => {
    return lat <= bounds.north && lat >= bounds.south &&
      lng <= bounds.east && lng >= bounds.west;
  };

  const saveManualRestaurant = async (formData) => {
    const restaurantData = { ...formData, city: city.name };
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      if (editingRestaurantId) {
        await axios.put(`/api/restaurants/${editingRestaurantId}`, restaurantData, { headers });
        alert("맛집이 수정되었습니다!");
      } else {
        await axios.post("/api/restaurants", restaurantData, { headers });
        alert("맛집이 저장되었습니다!");
      }
      await fetchSavedRestaurants();
      await fetchCategories(); // ✅ 새 카테고리 추가 시 목록 갱신
      setShowForm(false);
      setNewRestaurantPosition(null);
      setNewRestaurantAddress("");
      setInitialFormData(null);
      setEditingRestaurantId(null);
    } catch (error) {
      if (error.response?.status === 403) {
        alert("수정 권한이 없습니다.");
      } else {
        alert("맛집 저장에 실패했습니다.");
      }
    }
  };

  const handleEditRestaurant = (restaurant) => {
    setNewRestaurantPosition({ lat: restaurant.lat, lng: restaurant.lng });
    setNewRestaurantAddress(restaurant.address || '');
    setInitialFormData({
      name: restaurant.name,
      koreanName: restaurant.koreanName || '',
      memo: restaurant.memo || '',
      category: restaurant.category || '',  // ✅ 기존 카테고리 유지
    });
    setEditingRestaurantId(restaurant.id);
    setShowForm(true);
    setSelectedPlace(null);
  };

  const deleteRestaurant = async (id) => {
    if (window.confirm("정말 삭제하시겠습니까?")) {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.delete(`/api/restaurants/${id}`, { headers });
        alert("맛집이 삭제되었습니다.");
        await fetchSavedRestaurants();
        await fetchCategories(); // ✅ 삭제 후 카테고리 갱신
        setSelectedPlace(null);
      } catch (error) {
        if (error.response?.status === 403) {
          alert("삭제 권한이 없습니다.");
        }
      }
    }
  };

  const moveToLocation = (lat, lng) => {
    if (cityBounds && !isWithinBounds(lat, lng, cityBounds)) {
      alert(`${city.name} 내 위치만 이동 가능합니다!`);
      return;
    }
    setTargetCenter({ lat, lng });
    setTargetZoom(16);
  };

  const handleMapClick = async (event) => {
    if (!user) return;

    // ✅ 구글 기본 POI 팝업 막기
    event.stop?.();

    const lat = event.detail.latLng.lat;
    const lng = event.detail.latLng.lng;
    if (cityBounds && !isWithinBounds(lat, lng, cityBounds)) return;

    const placeId = event.detail.placeId;
    // ✅ 임시 디버그
    console.log("placeId:", placeId);
    console.log("event.detail:", event.detail);

    // ✅ placeId 있으면 Places API 자동 등록
    if (placeId && window.confirm("이 맛집을 자동 등록할까요?")) {
      registerFromPlace(placeId, city.name, async () => {
        await fetchSavedRestaurants();
        await fetchCategories();
      }, savedRestaurants);
      setSearchMarker(null);
      return; // 폼 열지 않음
    }

    setNewRestaurantPosition({ lat, lng });
    const address = await getAddressFromLatLng(lat, lng);
    setNewRestaurantAddress(address);
    setInitialFormData(placeId ? null : null);
    setEditingRestaurantId(null);
    setShowForm(true);
    setSearchMarker(null);
    setSelectedPlace(null);
  };

  const getDisplayName = (restaurant) => {
    if (restaurant.koreanName) return `${restaurant.koreanName} (${restaurant.name})`;
    return restaurant.name;
  };

  const canEditDelete = (restaurant) => {
    if (!user) return false;
    return user.kakaoId === ADMIN_KAKAO_ID || user.kakaoId === restaurant.kakaoId;
  };

  const fetchFavoriteIds = useCallback(async () => {
    if (!user) return;
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await axios.get("/api/favorites/ids", { headers });
      setFavoriteIds(res.data);
    } catch (e) {
      console.error("내 맛집 목록 조회 실패:", e);
    }
  }, [user]);

  useEffect(() => {
    fetchFavoriteIds();
  }, [fetchFavoriteIds]);

  const fetchVisitedPlaceIds = useCallback(async () => {
    if (!user) return;
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await axios.get("/api/visit", { headers });
      setVisitedIds(res.data);
    } catch (e) {
      console.error("방문 목록 조회 실패:", e);
    }
  }, [user]);

  useEffect(() => {
    fetchVisitedPlaceIds();
  }, [fetchVisitedPlaceIds]);

  useEffect(() => {
    if (!sharedRestaurantId || savedRestaurants.length === 0) return;

    const target = savedRestaurants.find(r => r.id === sharedRestaurantId);
    if (target) {
      setSelectedPlace({
        ...target,
        position: { lat: target.lat, lng: target.lng },
        isSaved: true,
      });
      setTargetCenter({ lat: target.lat, lng: target.lng });
      setTargetZoom(17);
    }
  }, [savedRestaurants, sharedRestaurantId]);

  const toggleFavorite = async (restaurantId) => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const isFav = favoriteIds.includes(restaurantId);
    try {
      if (isFav) {
        await axios.delete(`/api/favorites/${restaurantId}`, { headers });
        setFavoriteIds(prev => prev.filter(id => id !== restaurantId));
      } else {
        await axios.post(`/api/favorites/${restaurantId}`, {}, { headers });
        setFavoriteIds(prev => [...prev, restaurantId]);
      }
    } catch (e) {
      console.error("내 맛집 토글 실패:", e);
    }
  };
  const toggleVisit = async (restaurantId) => {
    console.log("🔍 toggleVisit 호출 restaurantId:", restaurantId, typeof restaurantId);
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await axios.post(`/api/visit/${restaurantId}`, {}, { headers });
      console.log("🔍 응답:", res.data);
      if (res.data.visited) {
        setVisitedIds(prev => {
          console.log("🔍 추가 전:", prev, "추가할 id:", restaurantId);
          return [...prev, restaurantId];
        });
      } else {
        setVisitedIds(prev => prev.filter(id => id !== restaurantId));
      }
    } catch (e) {
      console.error("방문 체크 토글 실패:", e);
    }
  };
  const requestMyLocation = useCallback(() => {
    if (isLocating) return;

    setIsLocating(true);
    setLocationError("");
    addLog("📍 현재 위치 요청 시작");

    if (!navigator.geolocation) {
      const msg = "이 기기에서는 위치 정보를 지원하지 않습니다.";
      setLocationError(msg);
      setIsLocating(false);
      addLog(`❌ ${msg}`);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const nextLocation = { lat, lng };

        setMyLocation(nextLocation);
        setLocationError("");

        if (cityBounds && !isWithinBounds(lat, lng, cityBounds)) {
          setIsLocating(false);
          setLocationError(`${city.name} 범위 밖의 위치입니다. 현재 위치 마커만 표시합니다.`);
          addLog(`⚠️ 도시 범위 밖 위치: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
          return;
        }

        setTargetCenter(nextLocation);
        setTargetZoom(16);
        setIsLocating(false);
        addLog(`✅ 성공: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
      },
      (error) => {
        setIsLocating(false);

        if (error.code === 1) {
          const msg = "위치 권한이 필요합니다. 설정으로 이동하시겠습니까?";
          if (window.confirm(msg)) {
            // ✅ 안드로이드 설정 화면으로 바로 이동하는 intent 스킴
            window.location.href = `intent://settings/applications/details?id=kr.ai.hynix.gr#Intent;scheme=package;package=com.android.settings;end;`;
          }
        } else {
          alert("위치를 가져올 수 없습니다.");
        }
      },
      { enableHighAccuracy: false, timeout: 20000 }
    );
  }, [isLocating, cityBounds, city.name]);

  const displayedRestaurants = searchResults
    ? searchResults  // ⭐ 검색 결과 있으면 검색 결과만
    : showMyOnly
      ? savedRestaurants.filter(r => r.kakaoId === user?.kakaoId || favoriteIds.includes(r.id))
      : savedRestaurants;

  return (
    <div style={{ height: "100vh", width: "100%", position: "relative" }}>
      <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        libraries={["places"]}
      >
        <Map
          defaultCenter={{ lat: city.lat, lng: city.lng }}
          defaultZoom={city.zoom}
          center={targetCenter}
          zoom={targetZoom}
          restriction={{ latLngBounds: cityBounds, strictBounds: true }}
          onCenterChanged={() => { if (targetCenter) setTimeout(() => setTargetCenter(null), 500); }}
          onZoomChanged={() => { if (targetZoom) setTimeout(() => setTargetZoom(null), 500); }}
          style={{ width: "100%", height: "100%" }}
          gestureHandling="greedy"
          clickableIcons={true}
          onClick={handleMapClick}
        //onLoad={(map) => setMapInstance(map)}
        >
          <MapInstanceSync setMapInstance={setMapInstance} />
          {myLocation && (
            <Marker
              position={myLocation}
              title="내 위치"
              icon={{
                url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
                scaledSize: new window.google.maps.Size(40, 40),
              }}
            />
          )}

          {displayedRestaurants.map((restaurant) => (
            <Marker
              key={`saved-${restaurant.id}-${visitedIds.includes(restaurant.id) ? 'visited' : 'normal'}`}
              position={{ lat: restaurant.lat, lng: restaurant.lng }}
              title={getDisplayName(restaurant)}
              // ✅ 방문한 맛집은 초록 마커
              icon={visitedIds.includes(restaurant.id)
                ? { url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png' }
                : undefined
              }
              onClick={() => setSelectedPlace({
                ...restaurant,
                position: { lat: restaurant.lat, lng: restaurant.lng },
                isSaved: true,
              })}
            />
          ))}

          {searchMarker && (
            <Marker
              position={{ lat: searchMarker.lat, lng: searchMarker.lng }}
              title={`${searchMarker.name} (클릭하여 맛집 등록)`}
              icon={{
                url: 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png',
                scaledSize: new window.google.maps.Size(45, 45),
              }}
              onClick={() => {
                console.log("🟡 searchMarker 클릭:", searchMarker);         // ✅ 추가
                console.log("🟡 searchMarker.placeId:", searchMarker.placeId); // ✅ 추가
                // ✅ DB 데이터면 등록 안 하고 정보 보여주기
                if (searchMarker.isDB) {
                  alert(
                    `✅ 이미 등록된 맛집입니다!\n\n` +
                    `${searchMarker.name}\n` +
                    `📍 ${searchMarker.address}`
                  );
                  return;
                }
                const pid = searchMarker.placeId;  // ✅ placeId 꺼내기
                if (pid && window.confirm("이 맛집을 자동 등록할까요?")) {
                  registerFromPlace(pid, city.name, async () => {
                    await fetchSavedRestaurants();
                    await fetchCategories();
                  }, savedRestaurants);
                  setSearchMarker(null);
                  return;  // 폼 안 열림
                }

                setSelectedPlace(null);
                setNewRestaurantPosition({ lat: searchMarker.lat, lng: searchMarker.lng });
                setNewRestaurantAddress(searchMarker.address || '');
                setInitialFormData({ name: searchMarker.name });
                setEditingRestaurantId(null);
                setShowForm(true);
                setSearchMarker(null);
              }}
            />
          )}

          {newRestaurantPosition && showForm && (
            <Marker
              position={newRestaurantPosition}
              icon={{ url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png' }}
            />
          )}
        </Map>

        {/* 검색창 */}
        <div style={{
          position: 'absolute', top: '70px',
          left: isMobile ? '10px' : '60px',
          right: isMobile ? '60px' : '20px',
          zIndex: 1000,
        }}>
          <RestaurantSearch
            map={mapInstance}
            onPlaceSelect={handlePlaceSelect}
            bounds={cityBounds}
            cityName={city.name}
            user={user}
          />
        </div>

        {/* ✅ 카테고리 필터 바 */}
        <div style={{
          position: 'absolute',
          left: isMobile ? '10px' : '60px',
          right: isMobile ? '10px' : '20px',
          bottom: isMobile ? '80px' : '20px',
          zIndex: 1000,
          display: 'flex',
          gap: '6px',
          overflowX: 'auto',
          padding: '8px 10px',
          backgroundColor: 'rgba(255,255,255,0.95)',
          borderRadius: '14px',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.12)',
          scrollbarWidth: 'none',
        }}>
          {categories
            .filter(cat => cat && cat.trim() !== "")   // ✅ null, 공백 제거
            .map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                style={{
                  flexShrink: 0,
                  padding: '6px 12px',
                  borderRadius: '20px',
                  border: 'none',
                  background: selectedCategory === cat ? '#4285f4' : 'white',
                  color: selectedCategory === cat ? 'white' : '#333',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                  whiteSpace: 'nowrap',
                }}
              >
                {cat}
              </button>
            ))}
        </div>

        {/* 팝업 */}
        {selectedPlace && !showForm && selectedPlace.isSaved && (
          <div style={{
            position: "absolute", bottom: "80px", left: "50%",
            transform: "translateX(-50%)",
            width: isMobile ? "88vw" : "320px",
            background: "white", borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
            zIndex: 1100, padding: "16px", boxSizing: "border-box",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
              <div style={{ flex: 1, paddingRight: "10px" }}>
                {selectedPlace.koreanName && (
                  <div style={{ fontSize: "16px", fontWeight: "bold", color: "#222", marginBottom: "3px" }}>
                    {selectedPlace.koreanName}
                  </div>
                )}
                <div style={{
                  fontSize: selectedPlace.koreanName ? "13px" : "16px",
                  fontWeight: selectedPlace.koreanName ? "normal" : "bold",
                  color: selectedPlace.koreanName ? "#888" : "#222",
                }}>
                  {selectedPlace.name}
                </div>
              </div>
              <button onClick={() => setSelectedPlace(null)} style={{
                background: "#f0f0f0", border: "none", borderRadius: "50%",
                width: "32px", height: "32px", fontSize: "16px", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, color: "#555",
              }}>✕</button>
            </div>

            {selectedPlace.address && (
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "6px", display: "flex", alignItems: "flex-start", gap: "4px" }}>
                <span>📍</span><span>{selectedPlace.address}</span>
              </div>
            )}

            {/* ✅ 카테고리 뱃지 추가 */}
            {selectedPlace.category && (
              <div style={{ marginBottom: "6px" }}>
                <span style={{
                  background: "#e8f0fe", color: "#4285f4",
                  fontSize: "11px", fontWeight: "bold",
                  padding: "3px 8px", borderRadius: "12px",
                }}>
                  {selectedPlace.category}
                </span>
              </div>
            )}

            {selectedPlace.memo && (
              <div style={{ fontSize: "12px", color: "#555", background: "#f9f9f9", borderRadius: "6px", padding: "8px", marginBottom: "10px" }}>
                📝 {selectedPlace.memo}
              </div>
            )}

            {canEditDelete(selectedPlace) && (
              <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                <button onClick={() => handleEditRestaurant(selectedPlace)} style={{ flex: 1, background: "#4285f4", color: "white", border: "none", padding: "10px", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "bold" }}>
                  ✏️ 수정
                </button>
                <button onClick={() => deleteRestaurant(selectedPlace.id)} style={{ flex: 1, background: "#ff4444", color: "white", border: "none", padding: "10px", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "bold" }}>
                  🗑️ 삭제
                </button>
              </div>
            )}

            {user && (
              <>
                <button onClick={() => toggleFavorite(selectedPlace.id)} style={{
                  width: "100%", marginTop: "10px", padding: "10px",
                  background: favoriteIds.includes(selectedPlace.id) ? "#fff3cd" : "#f8f9fa",
                  border: `1px solid ${favoriteIds.includes(selectedPlace.id) ? "#ffc107" : "#ddd"}`,
                  borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "bold",
                }}>
                  {favoriteIds.includes(selectedPlace.id) ? "⭐ 내 맛집 취소" : "☆ 내 맛집 등록하기"}
                </button>

                <button onClick={() => toggleVisit(selectedPlace.id)} style={{
                  width: "100%", marginTop: "8px", padding: "10px",
                  background: visitedIds.includes(selectedPlace.id) ? "#e8f5e9" : "#f8f9fa",
                  border: `1px solid ${visitedIds.includes(selectedPlace.id) ? "#4caf50" : "#ddd"}`,
                  borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "bold",
                  color: visitedIds.includes(selectedPlace.id) ? "#2e7d32" : "#333",
                }}>
                  {visitedIds.includes(selectedPlace.id) ? "✅ 다녀왔어요" : "📍 다녀왔어요?"}
                </button>

                <KakaoShareButton restaurant={selectedPlace} />
              </>
            )}
          </div>
        )}
      </APIProvider>

      {/* 뒤로 가기 */}
      <button onClick={onBack} style={{
        position: "absolute", top: "70px", left: "10px",
        background: "white", border: "none", borderRadius: "50%",
        width: "40px", height: "40px", cursor: "pointer",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)", zIndex: 1001,
        fontSize: "20px", display: "flex", alignItems: "center", justifyContent: "center",
      }} title="도시 변경">←</button>

      {/* 도시 정보 */}
      <div style={{
        position: "absolute", bottom: "20px", left: "20px",
        background: "rgba(255,255,255,0.9)", padding: "6px 12px",
        borderRadius: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        zIndex: 1000, fontSize: "12px", fontWeight: "bold", color: "#333",
      }}>
        📍 {city.name} · {selectedCategory !== "전체" ? `${selectedCategory} · ` : ""}{displayedRestaurants.length}곳
      </div>


      {/* 🍽️ 사이드바 토글 버튼 */}
      <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} style={{
        position: "absolute", top: "70px",
        right: isSidebarOpen ? (isMobile ? "calc(85vw + 5px)" : "310px") : "10px",
        zIndex: 1002, background: "#4285f4", border: "none", borderRadius: "8px",
        width: "44px", height: "44px", cursor: "pointer",
        boxShadow: "0 2px 8px rgba(0,0,0,0.2)", fontSize: "20px",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "right 0.3s ease",
      }}>
        {isSidebarOpen ? "✕" : "🍽️"}
      </button>
      <button
        onClick={requestMyLocation}
        disabled={isLocating}
        onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.96)")}
        onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        style={{
          position: "absolute",
          right: "10px",
          bottom: isMobile ? "190px" : "90px",
          zIndex: 1002,
          background: isLocating ? "#f1f3f4" : "white",
          border: "none",
          borderRadius: "50%",
          width: "48px",
          height: "48px",
          cursor: isLocating ? "default" : "pointer",
          boxShadow: "0 2px 10px rgba(0,0,0,0.22)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: isLocating ? 0.7 : 1,
        }}
        title={isLocating ? "현재 위치 확인 중" : "내 위치로 이동"}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="3.5" fill="#4285f4" />
          <path
            d="M12 2V5"
            stroke="#5f6368"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M12 19V22"
            stroke="#5f6368"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M2 12H5"
            stroke="#5f6368"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M19 12H22"
            stroke="#5f6368"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle
            cx="12"
            cy="12"
            r="7"
            stroke="#5f6368"
            strokeWidth="2"
          />
        </svg>
      </button>

      {/* 사이드바 */}
      {isSidebarOpen && (
        <div style={{
          position: "absolute", top: "0", right: "0", background: "white",
          padding: "20px", borderRadius: "8px 0 0 8px", height: "100%",
          overflowY: "auto", width: isMobile ? "85vw" : "300px",
          boxShadow: "-2px 0 8px rgba(0,0,0,0.15)", zIndex: 1001, boxSizing: "border-box",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <h3 style={{ margin: 0 }}>
              {searchResults
                ? `🔍 검색 결과 (${displayedRestaurants.length})`
                : showMyOnly
                  ? `⭐ 내 맛집 (${displayedRestaurants.length})`
                  : `${selectedCategory === "전체" ? "전체" : selectedCategory} (${displayedRestaurants.length})`}
            </h3>
            {searchResults && (
              <button onClick={() => setSearchResults(null)} style={{
                background: '#ff4444', color: 'white', border: 'none',
                borderRadius: '12px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer'
              }}>
                ✕ 검색 초기화
              </button>
            )}
          </div>

          {user && (
            <div style={{ display: "flex", gap: "6px", marginBottom: "12px" }}>
              <button onClick={() => setShowMyOnly(false)} style={{ flex: 1, padding: "7px", background: !showMyOnly ? "#4285f4" : "#f0f0f0", color: !showMyOnly ? "white" : "#333", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: "bold" }}>
                전체 보기
              </button>
              <button onClick={() => setShowMyOnly(true)} style={{ flex: 1, padding: "7px", background: showMyOnly ? "#4285f4" : "#f0f0f0", color: showMyOnly ? "white" : "#333", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: "bold" }}>
                ⭐ 내 맛집만
              </button>
            </div>
          )}

          {displayedRestaurants.length === 0 ? (
            <p style={{ color: "#999" }}>{showMyOnly ? "내 맛집이 없습니다." : "아직 저장된 맛집이 없습니다."}</p>
          ) : (
            displayedRestaurants.map((restaurant) => (
              <div key={restaurant.id} style={{ marginBottom: "10px", padding: "10px", border: "1px solid #ddd", borderRadius: "4px", background: "#f9f9f9" }}>
                {restaurant.koreanName && (
                  <h3 style={{ margin: "0 0 3px 0", fontSize: "15px" }}>{restaurant.koreanName}</h3>
                )}
                <h4 style={{ margin: "0 0 5px 0", fontSize: restaurant.koreanName ? "12px" : "14px", color: restaurant.koreanName ? "#666" : "#333" }}>
                  {restaurant.name}
                </h4>

                {/* ✅ 카테고리 뱃지 사이드바에도 추가 */}
                {restaurant.category && (
                  <span style={{
                    display: "inline-block", marginBottom: "4px",
                    background: "#e8f0fe", color: "#4285f4",
                    fontSize: "11px", fontWeight: "bold",
                    padding: "2px 7px", borderRadius: "10px",
                  }}>
                    {restaurant.category}
                  </span>
                )}

                {restaurant.address && <p style={{ fontSize: "12px", color: "#666", margin: "5px 0" }}>{restaurant.address}</p>}
                {restaurant.memo && <p style={{ margin: "5px 0", fontSize: "12px", color: "#888" }}>📝 {restaurant.memo}</p>}

                <div style={{ display: "flex", gap: "5px", marginTop: "8px" }}>
                  <button onClick={() => moveToLocation(restaurant.lat, restaurant.lng)} style={{ flex: 1, background: "#4285f4", color: "white", border: "none", padding: "5px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}>위치</button>
                  {canEditDelete(restaurant) && (
                    <>
                      <button onClick={() => handleEditRestaurant(restaurant)} style={{ flex: 1, background: "#34a853", color: "white", border: "none", padding: "5px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}>수정</button>
                      <button onClick={() => deleteRestaurant(restaurant.id)} style={{ flex: 1, background: "#ff4444", color: "white", border: "none", padding: "5px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}>삭제</button>
                    </>
                  )}
                </div>

                {user && (
                  <button onClick={() => toggleFavorite(restaurant.id)} style={{ width: "100%", marginTop: "6px", padding: "5px", background: favoriteIds.includes(restaurant.id) ? "#fff3cd" : "#f8f9fa", border: `1px solid ${favoriteIds.includes(restaurant.id) ? "#ffc107" : "#ddd"}`, borderRadius: "4px", cursor: "pointer", fontSize: "12px", fontWeight: "bold" }}>
                    {favoriteIds.includes(restaurant.id) ? "⭐ 내 맛집 취소" : "☆ 내 맛집 등록하기"}
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* 맛집 추가/수정 폼 */}
      {showForm && newRestaurantPosition && (
        <>
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1999 }}
            onClick={() => { setShowForm(false); setNewRestaurantPosition(null); setNewRestaurantAddress(""); setInitialFormData(null); setEditingRestaurantId(null); }}
          />
          <RestaurantForm
            position={newRestaurantPosition}
            address={newRestaurantAddress}
            cityName={city.name}
            initialData={initialFormData}
            isEditMode={!!editingRestaurantId}
            onSave={saveManualRestaurant}
            onCancel={() => { setShowForm(false); setNewRestaurantPosition(null); setNewRestaurantAddress(""); setInitialFormData(null); setEditingRestaurantId(null); }}
          />
        </>
      )}

      {/* 디버그 패널 */}
      {debugLog.length > 0 && (
        <div style={{
          position: "fixed", bottom: 80, left: 10, right: 10,
          background: "rgba(0,0,0,0.75)", color: "#fff",
          fontSize: 12, padding: 10, borderRadius: 8, zIndex: 9999
        }}>
          <button onClick={() => setDebugLog([])}
            style={{ float: "right", color: "#f88", background: "none", border: "none" }}>
            ✕ 닫기
          </button>
          {debugLog.map((log, i) => <div key={i}>{log}</div>)}
        </div>
      )}
    </div>
  );
};

export default MapView;
